import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Badge, Col, Form, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from 'hooks';

import { getErrorMessage } from '~libs/axiosWrapper';
import { dateDisplayFormat } from '~libs/dayjs';
import { useToast } from '~libs/toast';

import { Button, ConfirmDialog, ContainerHeader, ErrorResponse, Toolbar } from '~components';
import BulkActions from '~components/BulkActions';
import { DateInput, MultiSelect } from '~components/form-fields/inputs';
import { Row as TableViewRow, TableView } from '~components/table-view';
import { setTitle } from '~main/actions/login';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';

import { useCopyDexDataBulkMutation, useLazyGetDexReadinessQuery } from '../actions/dex';
import { readinessColumns } from '../forms/readinessList';
import { DexReadinessParams } from '../types';

const BULK_COPY_ACTION = 'copy-dex-data';
const bulkActionOptions = [{ value: BULK_COPY_ACTION, label: 'Copy DEX Data' }];

interface FilterValues {
  date_from: string;
  date_to: string;
  client_name: string;
  care_worker: string;
  service_type_id: number[];
}

const INITIAL_VALUES: FilterValues = {
  date_from: '',
  date_to: '',
  client_name: '',
  care_worker: '',
  service_type_id: [],
};

const PAGE_SIZE = 100;

// Both date fields carry the same range check, so either edit order surfaces the error on the
// field the user actually touched. The two wordings are kept distinct so each reads naturally
// under its own field - only one of them is ever rendered, see showFromError below.
const RANGE_ERROR_FROM = 'From date must be before to date';
const RANGE_ERROR_TO = 'To date must be after from date';

const validationSchema = Yup.object({
  date_from: Yup.string()
    .required('From date is required')
    .test('date-range', RANGE_ERROR_FROM, function (date_from) {
      const { date_to } = this.parent as FilterValues;
      return !date_from || !date_to || date_from <= date_to;
    }),
  date_to: Yup.string()
    .required('To date is required')
    .test('date-range', RANGE_ERROR_TO, function (date_to) {
      const { date_from } = this.parent as FilterValues;
      return !date_from || !date_to || date_from <= date_to;
    }),
});

const buildParams = (values: FilterValues, offset = 0): DexReadinessParams => {
  const params: DexReadinessParams = {
    date_from: values.date_from,
    date_to: values.date_to,
    limit: PAGE_SIZE,
    offset,
  };
  if (values.client_name) params.client_name = values.client_name;
  if (values.care_worker) params.care_worker = values.care_worker;
  if (values.service_type_id.length) params.service_type_id = values.service_type_id;
  return params;
};

// Mirrors a submitted search into the URL, so a refresh (or a shared link) lands back on the
// same result set rather than the blank filter form. A fresh URLSearchParams each submit -
// rather than mutating the existing one - so a filter the user has since cleared is dropped
// from the URL instead of lingering.
const buildSearchParams = (values: FilterValues): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('date_from', values.date_from);
  params.set('date_to', values.date_to);
  if (values.client_name) params.set('client_name', values.client_name);
  if (values.care_worker) params.set('care_worker', values.care_worker);
  values.service_type_id.forEach((id) => params.append('service_type_id', String(id)));
  return params;
};

const DexReadinessPage: React.FC = () => {
  useEffect(() => {
    setTitle('DEX Readiness Report');
  }, []);

  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const rawServiceTypes = useAppSelector(selectAppointmentServiceTypes);
  const dexServiceTypes = useMemo(() => rawServiceTypes.filter((s) => s.dex_code), [rawServiceTypes]);

  useEffect(() => {
    dispatch(getServiceTypes());
  }, [dispatch]);

  const [searchParams, setSearchParams] = useSearchParams();
  const prefillFrom = searchParams.get('date_from') ?? '';
  const prefillTo = searchParams.get('date_to') ?? '';
  const prefillClientName = searchParams.get('client_name') ?? '';
  const prefillCareWorker = searchParams.get('care_worker') ?? '';
  const prefillServiceTypeIds = searchParams
    .getAll('service_type_id')
    .map(Number)
    .filter((id) => !Number.isNaN(id));

  const [triggerReadiness, { data, isFetching, error }] = useLazyGetDexReadinessQuery();
  const [lastSubmittedValues, setLastSubmittedValues] = useState<FilterValues | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Guards the URL-prefill auto-trigger effect below so it only ever fires once per mount - a
  // manual submit marks it done too, since setSearchParams (below) changing date_from/date_to
  // from empty to populated would otherwise look exactly like a fresh URL prefill on the very
  // next render and fire a second, duplicate query right after the user's own search.
  const hasAutoTriggered = useRef(false);

  const formik = useFormik<FilterValues>({
    initialValues: {
      ...INITIAL_VALUES,
      date_from: prefillFrom,
      date_to: prefillTo,
      client_name: prefillClientName,
      care_worker: prefillCareWorker,
      service_type_id: prefillServiceTypeIds,
    },
    validationSchema,
    onSubmit: async (values) => {
      hasAutoTriggered.current = true;
      setLastSubmittedValues(values);
      setCurrentPage(1);
      setSelectedIds([]);
      setSearchParams(buildSearchParams(values), { replace: true });
      await triggerReadiness(buildParams(values, 0));
    },
  });

  const handleDateChange = (id: string, value: string | null) => {
    formik.setFieldValue(id, value ?? '');
    formik.setFieldTouched(id, true, false);
  };

  // An invalid range fails the range check on both fields at once, so without this the single
  // mistake would render two differently-worded messages in adjacent columns. Show it once:
  // under To normally, falling back to From when To was never touched (e.g. prefilled from the
  // URL while From is the field being edited). Required-field messages are unaffected - the
  // range check short-circuits on an empty value, so an empty form still flags both fields.
  const isRangeError = formik.errors.date_from === RANGE_ERROR_FROM && formik.errors.date_to === RANGE_ERROR_TO;
  const showFromError =
    !!formik.touched.date_from && !!formik.errors.date_from && !(isRangeError && !!formik.touched.date_to);
  const showToError = !!formik.touched.date_to && !!formik.errors.date_to;

  const handlePageChange = async (page: number) => {
    if (!lastSubmittedValues) return;
    setCurrentPage(page);
    setSelectedIds([]);
    await triggerReadiness(buildParams(lastSubmittedValues, (page - 1) * PAGE_SIZE));
  };

  useEffect(() => {
    if (!hasAutoTriggered.current && prefillFrom && prefillTo) {
      const prefillValues: FilterValues = {
        date_from: prefillFrom,
        date_to: prefillTo,
        client_name: prefillClientName,
        care_worker: prefillCareWorker,
        service_type_id: prefillServiceTypeIds,
      };
      if (validationSchema.isValidSync(prefillValues)) {
        hasAutoTriggered.current = true;
        setLastSubmittedValues(prefillValues);
        triggerReadiness(buildParams(prefillValues, 0));
      }
    }
    // prefillServiceTypeIds is a fresh array each render - the hasAutoTriggered guard above makes
    // that safe to omit here, since re-running this effect after the first successful trigger is
    // a no-op.
  }, [prefillFrom, prefillTo, prefillClientName, prefillCareWorker, triggerReadiness]);

  const summary = data?.summary;

  // One flat table row per incomplete appointment - no series grouping. The action cell resolves
  // row.appointment_id and every display column comes straight off the row.
  const readinessRows = useMemo((): TableViewRow[] => {
    if (!data) return [];
    return data.rows.map((row) => ({ ...row, id: row.appointment_id }));
  }, [data]);

  // TAV-3133: bulk copy selection. TableView has no per-row "not selectable" affordance, so
  // eligibility is enforced here - any ineligible id coming out of onRowSelectionChange (e.g. a
  // stale click before a refetch) is filtered out rather than allowed through to the bulk action.
  const eligibleRowIds = useMemo(
    () => new Set(readinessRows.filter((row) => row.dex_copy_eligible).map((row) => String(row.id))),
    [readinessRows],
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [copyDexDataBulk, { isLoading: isBulkCopying }] = useCopyDexDataBulkMutation();

  // Reconciles selectedIds whenever the result set changes on its own (e.g. a successful bulk
  // copy's tag invalidation refetch drops just-copied rows out of eligibility) - handlePageChange
  // and the search onSubmit clear the selection explicitly for user-driven result-set changes.
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => eligibleRowIds.has(id)));
  }, [eligibleRowIds]);

  // Several selected occurrences can share the same originating repeat appointment - the
  // confirmation dialog counts the distinct parents, not the selected occurrences themselves, so
  // it doesn't overstate how many source appointments are actually involved.
  const selectedParentCount = useMemo(() => {
    const selectedParents = readinessRows
      .filter((row) => selectedIds.includes(String(row.id)))
      .map((row) => row.parent_appointment_id);
    return new Set(selectedParents).size;
  }, [readinessRows, selectedIds]);

  const handleRowSelectionChange = (ids: (string | number)[]) => {
    setSelectedIds(ids.map(String).filter((id) => eligibleRowIds.has(id)));
  };

  const handleBulkAction = (action: string) => {
    if (action === BULK_COPY_ACTION) setBulkConfirmOpen(true);
  };

  const handleBulkConfirmClose = async (confirm: boolean) => {
    setBulkConfirmOpen(false);
    if (!confirm) return;

    try {
      const result = await copyDexDataBulk({ appointment_ids: selectedIds.map(Number) }).unwrap();
      const copiedCount = result.copied.length;
      const skippedCount = result.skipped.length;
      if (copiedCount > 0) {
        showToast('success', `Copied DEX data onto ${copiedCount} appointment${copiedCount !== 1 ? 's' : ''}`);
      }
      if (skippedCount > 0) {
        showToast(
          'info',
          `${skippedCount} appointment${skippedCount !== 1 ? 's' : ''} skipped — no longer eligible to copy`,
        );
      }
      // Mirrors DexReadinessActionCell's single-row handleCopy: an empty result either way still
      // needs its own feedback, or confirming a bulk action can silently do nothing from the
      // user's perspective (CodeRabbit finding, MR !1428).
      if (copiedCount === 0 && skippedCount === 0) {
        showToast('info', 'No DEX data was copied — the selected appointments may have changed elsewhere');
      }
      setSelectedIds([]);
    } catch (error) {
      showToast('danger', getErrorMessage(error));
    }
  };

  // TAV-3251: leads with the irreversible-action warning, in its own Alert (matching
  // DexCopyConfirmDialog's pattern of a single ReactNode passed with useList={false}), rather than
  // as the 4th item in a plain bullet list - and drops "occurrence" in favour of "single
  // appointment", which providers are already familiar with, throughout.
  const bulkConfirmContent = (
    <div data-cy="dex-bulk-confirm-body">
      <Alert color="danger" className="mb-3" data-cy="dex-bulk-confirm-warning">
        <strong>This action cannot be undone</strong> - once copied, a single appointment cannot be copied onto
        again.
      </Alert>
      <p>
        You&apos;re about to copy DEX data from {selectedParentCount} originating repeat appointment(s) onto{' '}
        {selectedIds.length} selected single appointment(s). This writes DEX reporting data directly onto each
        selected appointment, using the values recorded on the originating repeat.
      </p>
      <ul className="mb-0">
        <li>
          This is the data submitted to the Department of Health, Disability and Ageing (DoHDA) via DEX - please
          check the source repeat appointments are correct before continuing.
        </li>
        <li>
          Only single appointment(s) without existing DEX data will be updated. Single appointment(s) that
          already have DEX data won&apos;t be touched.
        </li>
        <li>
          Only eligible fields will be impacted - if the originating repeat appointment has more information
          than a single appointment needs, only the required items will be copied over.
        </li>
        <li>Every copy is recorded on the receiving single appointment&apos;s activity log.</li>
      </ul>
    </div>
  );

  return (
    <div className="dex-readiness-page p-2" data-cy="dex-readiness-page">
      <ContainerHeader dataCy="dex-readiness-header">DEX Readiness Report</ContainerHeader>

      <div className="p-2 ps-4 pe-4">
        <p className="text-muted mb-3">
          Find appointments with missing Stage 3 DEX data before running the DEX export.
        </p>

        <Form onSubmit={formik.handleSubmit} data-cy="dex-readiness-filters">
          <Row>
            <Col md={3}>
              <FormGroup>
                <Label for="date_from">From Date</Label>
                <DateInput
                  type="date"
                  id="date_from"
                  name="date_from"
                  value={formik.values.date_from}
                  onChange={handleDateChange}
                  dataCy="dex-readiness-from-date"
                  displayFormat={dateDisplayFormat}
                />
                {showFromError && (
                  <div className="invalid-feedback d-block" data-cy="dex-readiness-from-date-error">
                    {formik.errors.date_from}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="date_to">To Date</Label>
                <DateInput
                  type="date"
                  id="date_to"
                  name="date_to"
                  value={formik.values.date_to}
                  onChange={handleDateChange}
                  dataCy="dex-readiness-to-date"
                  displayFormat={dateDisplayFormat}
                />
                {showToError && (
                  <div className="invalid-feedback d-block" data-cy="dex-readiness-to-date-error">
                    {formik.errors.date_to}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="client_name">Client Name</Label>
                <Input
                  type="text"
                  id="client_name"
                  name="client_name"
                  value={formik.values.client_name}
                  onChange={formik.handleChange}
                  placeholder="Search client..."
                  data-cy="dex-readiness-client-name"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="care_worker">Care Worker</Label>
                <Input
                  type="text"
                  id="care_worker"
                  name="care_worker"
                  value={formik.values.care_worker}
                  onChange={formik.handleChange}
                  placeholder="Search care worker..."
                  data-cy="dex-readiness-care-worker"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="service_type_id">Service Type</Label>
                <div data-cy="dex-readiness-service-type">
                  <MultiSelect
                    type="select"
                    id="service_type_id"
                    name="service_type_id"
                    value={formik.values.service_type_id}
                    options={dexServiceTypes}
                    placeholder="All"
                    setFieldValue={formik.setFieldValue}
                  />
                </div>
              </FormGroup>
            </Col>
            <Col md={3} className="d-flex align-items-end pb-3">
              <Button
                type="submit"
                color="success"
                disabled={isFetching}
                data-cy="dex-readiness-search-button"
              >
                {isFetching ? <Spinner size="sm" /> : 'Search'}
              </Button>
            </Col>
          </Row>
        </Form>

        {/* TAV-3133: Beth Kairys review feedback - only some rows are eligible for the select-all
            checkbox / copy action, with no visible reason why. This states the eligibility rule
            up front, independent of whether a search has been run yet. */}
        <Alert color="info" className="mt-2" data-cy="dex-readiness-bulk-eligibility-note">
          <strong>Not every row supports bulk actions.</strong> Only appointments eligible for a DEX copy
          can be selected or bulk-copied.
        </Alert>

        {error && (
          <ErrorResponse message={getErrorMessage(error)} dataCy="dex-readiness-error" />
        )}

        {data && summary && !error && (
          <Alert
            color={summary.total === 0 ? 'success' : 'warning'}
            className="mt-2"
            data-cy="dex-readiness-summary"
          >
            {summary.total === 0 ? (
              'All DEX data complete - no appointments have missing Stage 3 data in the selected date range.'
            ) : (
              <>
                <strong>{summary.total}</strong> incomplete appointment{summary.total !== 1 ? 's' : ''} found.{' '}
                <Badge color="danger" className="me-1" data-cy="dex-readiness-no-record-badge">
                  {summary.no_session_record} no DEX data
                </Badge>
                <Badge color="warning" className="me-1" data-cy="dex-readiness-partial-badge">
                  {summary.partial_data} partial data
                </Badge>
                <Badge color="warning" data-cy="dex-readiness-mac-id-badge">
                  {summary.missing_mac_id ?? 0} appointment
                  {(summary.missing_mac_id ?? 0) !== 1 ? 's' : ''} missing My Aged Care ID
                </Badge>
              </>
            )}
          </Alert>
        )}
      </div>

      <Toolbar
        id="dex-readiness"
        rightCluster={
          <BulkActions
            id="dex-readiness-bulk-actions"
            actionOptions={bulkActionOptions}
            onAction={handleBulkAction}
            selectedIds={selectedIds}
          />
        }
      />

      <div className="px-2 pb-2">
        <TableView
          id="dex-readiness-table"
          columns={readinessColumns}
          data={readinessRows}
          isLoading={isFetching || isBulkCopying}
          emptyMessage={
            data ? 'No appointments with missing DEX data in this date range.' : 'Enter a date range and click Search.'
          }
          readonly
          striped
          allowPagination
          paginationType="pages"
          totalCount={data?.total ?? 0}
          pageSize={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          canSelect
          selectedIds={selectedIds}
          onRowSelectionChange={handleRowSelectionChange}
          isRowDisabled={(row: TableViewRow) => !row.dex_copy_eligible}
          data-cy="dex-readiness-table"
        />
      </div>

      <ConfirmDialog
        isOpen={bulkConfirmOpen}
        messages={bulkConfirmContent}
        onClose={handleBulkConfirmClose}
        useList={false}
        confirmColor="danger"
      />
    </div>
  );
};

export default DexReadinessPage;
